import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';

import { ActionData } from '@bonusly/common-action-input';
import { useDispatch } from '@bonusly/events';
import { useHttpClient } from '@bonusly/http-client';

import { GiveBoxEvent } from '../events';
import { createTrackingData } from '../utils/analytics';
import { createBonusData } from '../utils/create-bonus-data';
import { parseErrorMessage } from '../utils/error';
import { useBonusBoxAnalytics } from './use-bonus-box-analytics';

interface UseGiveRecognitionConfig {
	onError?: (error: unknown) => void;
	onSuccess?: VoidFunction;
	via?: string;
	msTeamsMeetingId?: string;
	wrapPost?: boolean;
	draftAssistInteractionIds?: string[];
}

export const useGiveRecognition = ({ onError, onSuccess, via, msTeamsMeetingId, wrapPost, draftAssistInteractionIds }: UseGiveRecognitionConfig = {}) => {
	const http = useHttpClient('v2');
	const analytics = useBonusBoxAnalytics();
	const dispatch = useDispatch();

	// Keep a ref so the mutation function always reads the latest value at call time,
	// even when draftAssistInteractionIds is updated after the hook first renders.
	const draftAssistInteractionIdsRef = useRef(draftAssistInteractionIds);
	draftAssistInteractionIdsRef.current = draftAssistInteractionIds;

	const { mutate, isLoading: isSubmitting } = useMutation<{ data: { result: any } }, unknown, ActionData, unknown>(
		(action) => {
			const currentIds = draftAssistInteractionIdsRef.current;
			const data = {
				bonus: {
					...createBonusData({ action }),
					wrap_post: wrapPost,
					...(currentIds?.length ? { draft_assist_interaction_ids: currentIds } : {}),
				},
			};

			const params = {
				include_children: true,
			};

			if (via) {
				Object.assign(params, { application_name: via });
			}

			if (msTeamsMeetingId) {
				Object.assign(params, { ms_teams_meeting_id: msTeamsMeetingId });
			}

			return http.post('/bonuses', data, { params });
		},
		{
			onError: (err: unknown) => {
				analytics.trackSubmitFailure({
					reason: parseErrorMessage(err),
				});

				onError?.(err);
			},
			onSuccess: (data, vars) => {
				const bonus = data.data.result;
				const totalAmount = vars.totalAmount;

				dispatch(GiveBoxEvent.Created, { bonus, totalAmount });
				analytics.trackSubmitSuccess(createTrackingData(vars));
				onSuccess?.();
			},
		}
	);

	return {
		isSubmitting,
		onSubmit: mutate,
	};
};
